import {IReportDataObject, emptyReportDataObject} from "../../entities/reports/IReportData";
import {FetchState} from "../../entities/FetchState";
import { Reducer } from "redux";
import { all, call, fork, put, takeEvery } from "redux-saga/effects";
import { IError } from "../../utils/callGraphQl";
import Either from "../../utils/Either";
import {callApiWithResult, ICallResult} from "../../utils/callApi";

/**********************************************************
 * Enums
 **********************************************************/
// Friendly enum names instead of database event types
export enum UserAccessLevel {
    RestrictedReports = "User.Access.GrantedToRestrictedReports.v1",
    UserManagement = "User.Access.GrantedToUserManagement.v1",
    VaphTokenManagement = "User.Access.GrantedToVaphTokenManagement.v1",
    AutomaticTasks = "User.Access.GrantedToAutomaticTasks.v1"
}

/**********************************************************
 * API Data
 **********************************************************/
export interface IData {
    streamId: string;
    uuid: string;
    userName: string;
    firstName: string;
    lastName: string;
    role: number;
    email: string;
    companyId: string;
    companyStreamId: string;
    active: boolean;
    hasAccessToRestrictedReports: boolean;
    permissions: UserAccessLevel[];
}

export const defaultUserInfo: IData = {
    streamId: '',
    uuid: '',
    userName: '',
    firstName: '',
    lastName: '',
    role: 0,
    email: '',
    companyId: '',
    companyStreamId: '',
    active: false,
    hasAccessToRestrictedReports: false,
    permissions: []
};

export const defaultUserInfoObject = emptyReportDataObject<IData>(defaultUserInfo);

/**********************************************************
 * State
 **********************************************************/

export interface IState {
    UserInfo: IReportDataObject<IData>;
}

export const InitialState: IState = {
    UserInfo: defaultUserInfoObject
}

/**********************************************************
 * Action Types
 **********************************************************/
export enum ActionTypes {
    FetchData = "user.UserInfo Fetch",
    FetchDataSuccess = "user.UserInfo FetchSuccess",
    FetchDataFailed = "user.UserInfo FetchFailed"
}

/**********************************************************
 * Actions & Creators
 **********************************************************/
export interface IFetchData {
    type: ActionTypes.FetchData
}
export interface IFetchDataSuccess {
    type: ActionTypes.FetchDataSuccess,
    payload: { data: IData }
}
export interface IFetchDataFailed {
    type: ActionTypes.FetchDataFailed,
    payload: { errorMessage: string }
}

type Actions = IFetchData | IFetchDataSuccess | IFetchDataFailed;

export class ActionCreators {
    public static FetchData(): IFetchData {
        return { 
            type: ActionTypes.FetchData
        };
    }
    public static FetchDataSuccess(newData: IData): IFetchDataSuccess {
        return {
            type: ActionTypes.FetchDataSuccess,
            payload: { data: newData }
        };
    }
    public static FetchDataFailed(err: string): IFetchDataFailed {
        return {
            type: ActionTypes.FetchDataFailed,
            payload: { errorMessage: err }
        };
    }
}

/**********************************************************
 * Reducer
 **********************************************************/
export const Reducers: Reducer<IState, Actions> = (state = InitialState, action) => {
    switch (action.type) {
        case ActionTypes.FetchData:
            return { 
                ...state, 
                UserInfo: {
                    ...state.UserInfo,
                    state: FetchState.Busy
                }
            };
        case ActionTypes.FetchDataSuccess:
            return { 
                ...state, 
                UserInfo: {
                    ...state.UserInfo,
                    state: FetchState.Success,
                    data: action.payload.data,
                    fetchDate: new Date()
                }
            };
        case ActionTypes.FetchDataFailed:
            return { 
                ...state, 
                UserInfo: {
                    ...state.UserInfo,
                    state: FetchState.Error,
                    errorMessage: action.payload.errorMessage
                }
            };
        default:
            return state;
    }
}

/**********************************************************
 * Helper Functions
 **********************************************************/

// Helper to check if user has a specific access level
export const hasAccessLevel = (userInfo: IData, accessLevel: UserAccessLevel): boolean => {
    return userInfo.permissions.includes(accessLevel);
};

// Helper to check if user has access to a subset of the settings (replaces hasAccessToSettings boolean)
export const hasSettingsAccess = (userInfo: IData): boolean => {
    const settingsAccessLevels = [
        UserAccessLevel.UserManagement,
        UserAccessLevel.VaphTokenManagement,
    ];
    return hasAnyAccessLevel(userInfo, settingsAccessLevels);
}

// Helper to check multiple access levels (user needs ALL of them)
export const hasAllAccessLevels = (userInfo: IData, accessLevels: UserAccessLevel[]): boolean => {
    return accessLevels.every(level => userInfo.permissions.includes(level));
};

// Helper to check if user has at least one of the access levels
export const hasAnyAccessLevel = (userInfo: IData, accessLevels: UserAccessLevel[]): boolean => {
    return accessLevels.some(level => userInfo.permissions.includes(level));
};

/**********************************************************
 * API Response Type Mapping
 **********************************************************/
interface IApiResponse {
    streamId: string;
    uuid: string;
    userName: string;
    firstName: string;
    lastName: string;
    role: number;
    email: string;
    companyId: string;
    companyStreamId: string;
    active: boolean;
    hasAccessToRestrictedReports: boolean;
    permissions: string[];
}

function mapApiResponseToData(apiResponse: IApiResponse): IData {
    return {
        streamId: apiResponse.streamId,
        uuid: apiResponse.uuid,
        userName: apiResponse.userName,
        firstName: apiResponse.firstName,
        lastName: apiResponse.lastName,
        role: apiResponse.role,
        email: apiResponse.email,
        companyId: apiResponse.companyId,
        companyStreamId: apiResponse.companyStreamId,
        active: apiResponse.active,
        hasAccessToRestrictedReports: apiResponse.hasAccessToRestrictedReports,
        permissions: apiResponse.permissions
            .filter((p): p is UserAccessLevel => 
                Object.values(UserAccessLevel).includes(p as UserAccessLevel)
            )
    };
}

/**********************************************************
 * Sagas
 **********************************************************/

function* handleFetchData(_action: IFetchData) {
    try {
        const response : Either<IError, ICallResult<IApiResponse>> = yield call(callApiWithResult, 'GET', '/v1/Users/me');
        yield response
            .FlatMap(success => {
                const mappedData = mapApiResponseToData(success.result);
                return put(ActionCreators.FetchDataSuccess(mappedData) as any);
            })
            .Reduce(error => put(ActionCreators.FetchDataFailed(error.text) as any))
    } catch (err) {
        yield put(ActionCreators.FetchDataFailed(JSON.stringify(err)))
    }
}

function* watchAll() {
    yield takeEvery(ActionTypes.FetchData, handleFetchData);
}

export function* sagas() {
    yield all([fork(watchAll)]);
}

